import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { ArrowRight, Command, GitBranch, Loader2, ShieldCheck } from 'lucide-react'
import { useUser } from '@/hooks/useUser'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { BrandMark } from '@/components/ui/brand-mark'
import { useAuthLoadingWatchdog } from '@/auth/useAuthLoadingWatchdog'
import { clearLogtoSessionStorage } from '@/auth/recoverSession'

const AUTH_SETTLE_MS = 250

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

export function LoginPage() {
  const { signIn, isLoading, isAuthenticated } = useUser()
  const navigate = useNavigate()
  const [confirmedSignedIn, setConfirmedSignedIn] = useState(false)

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      // Intentional synchronous reset to cancel any stale "confirmed
      // signed-in" promotion when Logto flips back to loading/unauth mid-
      // rehydrate. See _authenticated.tsx for the symmetric pattern.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConfirmedSignedIn(false)
      return
    }
    const timer = setTimeout(() => setConfirmedSignedIn(true), AUTH_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [isLoading, isAuthenticated])

  useEffect(() => {
    if (confirmedSignedIn) {
      navigate({ to: '/dashboard' })
    }
  }, [confirmedSignedIn, navigate])

  // If the Logto SDK is wedged on isLoading=true, never block the user from
  // seeing the Sign-in button — they got here precisely *because* they want
  // to start a fresh sign-in. Clearing the cached session before the click
  // matches the recovery-card path and prevents the stuck rehydrate from
  // attaching itself to the fresh attempt.
  const stuck = useAuthLoadingWatchdog(isLoading && !isAuthenticated)
  const showSignInUi = !isLoading || stuck

  if (!showSignInUi) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10">
      <div className="grid w-full max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <section className="relative hidden overflow-hidden rounded-lg border border-white/10 bg-white/[0.035] p-8 shadow-floating backdrop-blur-xl lg:block">
          <BrandMark size="lg" />
          <div className="mt-16 max-w-xl">
            <p className="cockpit-eyebrow">Bosun control plane</p>
            <h1 className="mt-3 text-4xl font-semibold leading-tight">
              Operational AI workspaces, provisioned with evidence.
            </h1>
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              Coordinate projects, Hermes roles, infrastructure targets, and
              delivery state from a single command surface.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-2">
            <LoginSignal icon={Command} label="Runbooks" />
            <LoginSignal icon={GitBranch} label="Repos" />
            <LoginSignal icon={ShieldCheck} label="Secrets" />
          </div>
        </section>
        <div className="relative w-full max-w-sm space-y-6 justify-self-center lg:justify-self-auto">
          <div className="flex justify-center lg:hidden">
            <BrandMark size="lg" />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-lg">Sign in to Bosun</CardTitle>
            <CardDescription>
              Continue with your work account to manage project operations,
              kanban work, and deployment evidence.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              size="lg"
              onClick={() => {
                // If we got here because the SDK was wedged, wipe the cached
                // session so the fresh sign-in handshake starts clean — the
                // stuck rehydrate promise can't poison the new flow.
                if (stuck) clearLogtoSessionStorage()
                void signIn()
              }}
              disabled={isLoading && !stuck}
            >
              Sign in
              <ArrowRight />
            </Button>
          </CardContent>
          <CardFooter className="justify-center pt-0 text-xs text-muted-foreground">
            Production uses Microsoft Entra single sign-on.
          </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  )
}

function LoginSignal({
  icon: Icon,
  label,
}: {
  icon: typeof Command
  label: string
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-3">
      <Icon className="mb-3 size-4 text-primary" />
      <p className="text-xs font-medium">{label}</p>
    </div>
  )
}
